import React, { ReactElement } from "react";
import { useGetQueueListQuery, User } from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface QueuePickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        text?: (TIsMulti extends true ? string[] : string)
    ) => void;
}

const Queues = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable,
}: QueuePickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetQueueListQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<User>[] : Option<User>;
    type ValueType = TIsMulti extends true ? Option<User>[] : Option<User>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.queues?.map(queue => ({
        label: queue.name as string,
        value: queue.id.toString() as string,
    })) ?? [];

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const vals = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<User>).value) as Parameters<typeof onSelected>[0];

        const texts = (Array.isArray(selected)
            ? selected.map(row => values.find(val => val.label === row)?.label)
            : (selected as Option<User>).label) as Parameters<typeof onSelected>[0];
        onSelected(vals, texts);
    };

    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={values ?? []}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}

        />
    );
});

Queues.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
    className: undefined,
};

export default Queues as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: QueuePickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Queues>;
